import type { GetStaticPropsContext, GetStaticPropsResult } from 'next/types'
import type {
  AboutUsQuery,
  AboutUsQueryVariables,
  AllPagesQuery,
  Image,
  LocaleString,
} from '@/.generated/sanityTypes/graphql'

import React from 'react'
import { useRouter } from 'next/router'

import PageSection from '@/components/pageSection'
import Layout from '@/components/layout'
import Card from '@/components/card'
import { request } from '@/lib/sanityClient'
import { aboutUs as aboutUsQuery } from './about.sanity.graphql'
import { isLocalString } from '../lib/typeHelper'
import { getAllPages, getPageQueryVariables } from '@/lib/query'

type AboutProps = {
  data: AboutUsQuery
  messages: unknown
  isPreview: boolean
  allPages: AllPagesQuery
}

export const About = ({ data, isPreview, allPages }: AboutProps) => {
  const { locale = 'de' } = useRouter()
  const page = data.allPage[0]

  return (
    <Layout
      allPages={allPages}
      isPreview={isPreview}
      title={
        (isLocalString(page?.pageHeader?.sectionTitle) &&
          page?.pageHeader?.sectionTitle[locale as keyof LocaleString]) ||
        undefined
      }
      headerFiles={
        [
          ...(page?.image ? [page?.image] : []),
          ...(page?.additionalImages || []),
        ] as Image[]
      }
    >
      {page?.pageHeader && (
        <Card headerData={page.pageHeader} contentData={page} />
      )}
      {page?.aboutUsSections?.map((section: any) => {
        return <PageSection key={section.id || section._id} data={section} />
      })}
    </Layout>
  )
}

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<AboutProps>> {
  const isPreview = context.preview || false
  const queryVariables = getPageQueryVariables('about')
  const pageData = await request<AboutUsQuery, AboutUsQueryVariables>(
    aboutUsQuery,
    queryVariables,
    isPreview,
  )

  if (pageData.errors)
    throw new Error(`graphql Error occured: ${pageData.errors[0].message}`)

  return {
    props: {
      data: pageData.data,
      messages: (await import(`@/translation/${context.locale}.json`)).default,
      isPreview,
      allPages: await getAllPages(),
    },
  }
}

export default About
